import { Component, Input, OnInit, ViewChild, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';
import { FarmManagementService } from '../../services/farm-management.service';
import { FieldDashboardDataService } from '../field-dashboard-data.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-field-map',
  templateUrl: './field-map.component.html',
  styleUrls: ['./field-map.component.css'],
  standalone: false
})
export class FieldMapComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() fieldId: string;
  @Input() farmId: string;
  @ViewChild('canvas', { static: false }) canvas: ElementRef<HTMLCanvasElement>;
  
  private ctx: CanvasRenderingContext2D;
  private fieldPoints: any[] = [];
  private subscription: Subscription;
  private canvasReady = false;

  constructor(
    private farmService: FarmManagementService,
    private dataService: FieldDashboardDataService
  ) {}

  ngOnInit(): void {
    this.subscription = this.dataService.fieldData$.subscribe(data => {
      if (data.fieldId && data.farmId) {
        this.fieldId = data.fieldId;
        this.farmId = data.farmId;
        
        if (data.boundaryPoints && data.boundaryPoints.length > 0) {
          this.fieldPoints = data.boundaryPoints;
          if (this.canvasReady) {
            this.drawFieldOutline();
          }
        } else if (this.canvasReady) {
          this.loadFieldBoundary();
        }
      }
    });
  }

  ngAfterViewInit(): void {
    this.ctx = this.canvas.nativeElement.getContext('2d');
    this.canvasReady = true;
    
    const cachedData = this.dataService.getFieldData();
    if (cachedData.boundaryPoints && cachedData.boundaryPoints.length > 0) {
      this.fieldPoints = cachedData.boundaryPoints;
      this.drawFieldOutline();
    } else if (this.fieldId && this.farmId) {
      this.loadFieldBoundary();
    }
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  loadFieldBoundary(): void {
    this.farmService.getFarmPolygon(Number(this.farmId)).subscribe((fields: any[]) => {
      const field = fields.find(f => f.id === Number(this.fieldId));
      if (field && field.boundry) {
        const coordinates = field.boundry.features[0].geometry.coordinates;
        const points = [];

        coordinates.forEach((coordinate) => {
          if (coordinate.length === 1) {
            coordinate[0].forEach(latLng => {
              points.push({ lat: Number(latLng[1]), lng: Number(latLng[0]) });
            });
          } else if (coordinate[0].length === 2) {
            coordinate.forEach(coord => {
              points.push({ lat: Number(coord[1]), lng: Number(coord[0]) });
            });
          } else {
            coordinate.forEach(coord => {
              coord.forEach(latLng => {
                points.push({ lat: Number(latLng[1]), lng: Number(latLng[0]) });
              });
            });
          }
        });
        
        this.fieldPoints = points;
        this.dataService.updateFieldData({ boundaryPoints: points });
        setTimeout(() => this.drawFieldOutline(), 100);
      }
    });
  }

  drawFieldOutline(): void {
    if (!this.ctx || this.fieldPoints.length === 0) return;

    const canvas = this.canvas.nativeElement;
    const width = canvas.width;
    const height = canvas.height;

    const lats = this.fieldPoints.map(p => p.lat);
    const lngs = this.fieldPoints.map(p => p.lng);
    
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    
    const latRange = maxLat - minLat;
    const lngRange = maxLng - minLng;
    
    const padding = 20;
    const drawWidth = width - (padding * 2);
    const drawHeight = height - (padding * 2);

    this.ctx.clearRect(0, 0, width, height);
    this.ctx.strokeStyle = '#8dc63f';
    this.ctx.lineWidth = 3;
    this.ctx.beginPath();

    this.fieldPoints.forEach((point, index) => {
      const x = ((point.lng - minLng) / lngRange) * drawWidth + padding;
      const y = ((maxLat - point.lat) / latRange) * drawHeight + padding;
      
      if (index === 0) {
        this.ctx.moveTo(x, y);
      } else {
        this.ctx.lineTo(x, y);
      }
    });

    this.ctx.closePath();
    this.ctx.stroke();
  }
}
